<template>
    <b-row v-if="questionary.amountOfQuestions > 0">
        <b-col cols="12">
            <h4>
                {{ questionary.title }}
            </h4>
        </b-col>
        <b-col cols="6" v-for="q in questions" :key="q.id">
            <fg-input :label="q.questionText">
                <input class="form-control" type="text" v-model="q.answer.value" :disabled="disable">
            </fg-input>
        </b-col>
    </b-row>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Questionary } from '@/models/Questionary';
import axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Prescription } from '@/models/Prescription';

@Component({
    name: "QuestionaryComponent"
})
export default class QuestionaryComponent extends Vue {
    @Prop() private categoryID!: number;
    @Prop() private prescription!: Prescription;
    @Prop({ default: false }) private newMode!: boolean;
    @Prop({ default: false }) private disable!: boolean;

    private questionary: Questionary = new Questionary();
    private questions: Array<Questionary.Question> = [];
    // Snapshot of answer values captured after load so the prescription page can detect whether the
    // operator actually changed anything before deciding to submit this category's answers.
    private originalAnswersSnapshot = "";
    // True once answers have successfully loaded and a baseline snapshot was captured. Guards isDirty()
    // so a failed or in-flight load can never report the category as changed — which would otherwise
    // bundle a blank payload that overwrites the saved answers with nulls.
    private answersLoaded = false;
    // The snapshot captured at the moment getChangedQuestionary() built the save payload. markSaved()
    // re-baselines to this instead of live state, so an answer the operator edits while the save is in
    // flight stays dirty and is re-submitted on the next save rather than being silently dropped.
    private pendingSnapshot: string | null = null;

    created() {
        this.load();
    }

    beforeDestroy() {
        this.clear();
    }

    @Watch('categoryID') categoryIdChanged() {
        this.load();
    }

    clear() {
        this.questionary = new Questionary();
        this.questions = [];
        this.originalAnswersSnapshot = "";
        this.answersLoaded = false;
        this.pendingSnapshot = null;
    }

    load() {
        if (!this.categoryID) return;

        this.clear();
        axios.get<Questionary>(`/DrugCategory/${this.categoryID}/custom-questions`)
            .then(response => {
                this.questionary = Object.assign(new Questionary(), response.data);

                if (!this.questionary.id) return;

                if (this.prescription.hasFullId) {
                    axios.get<Array<Questionary.Answer>>(`/Prescription/${this.prescription.rxID}/get-answers/${this.questionary.id}`)
                        .then(response => {
                            this.questionary.answers = response.data.map(a => Object.assign(new Questionary.Answer(), a));
                            this.questions = this.questionary.orderedQuestionsAsc;
                            this.captureAnswersSnapshot();
                        })
                        .catch(error => {
                            console.error('Error while getting questionary answers', {
                                categoryID: this.categoryID,
                                error,
                                response: error?.response
                            });
                            // answersLoaded stays false, so this category is never treated as "changed"
                            // and is never submitted until its answers actually load. Surface the failure
                            // (the load was previously silent) so the operator knows answers are missing.
                            this.$notification(NotificationOptions.warning(
                                `Could not load answers for ${this.questionary.title || 'this drug category'}. Reload the page before answering.`));
                        });
                } else {
                    this.questions = this.questionary.orderedQuestionsAsc;
                    this.captureAnswersSnapshot();
                }
            })
            .catch(error => {
                console.error('Error while getting custom questionary', {
                    categoryID: this.categoryID,
                    error,
                    response: error?.response
                });
                this.$notification(NotificationOptions.error(error));
            });
    }

    private answersSnapshot(): string {
        return JSON.stringify(this.questions.map(q => ({ id: q.id, value: q.answer?.value ?? "" })));
    }

    private captureAnswersSnapshot() {
        this.originalAnswersSnapshot = this.answersSnapshot();
        this.answersLoaded = true;
    }

    /** True when this category has questions and the operator changed at least one answer since load. */
    isDirty(): boolean {
        // Never dirty until answers have loaded — otherwise a failed/in-flight load (this.questions still
        // empty) would compare against an empty baseline and falsely report a change.
        if (!this.answersLoaded) return false;
        if (!(this.questionary?.id && this.questionary?.amountOfQuestions > 0)) return false;
        return this.answersSnapshot() !== this.originalAnswersSnapshot;
    }

    /**
     * Returns this category's questionary (with current answers applied) for inclusion in the Rx save
     * payload, or null when nothing changed. Lets the prescription page bundle only modified answers
     * into a single Rx POST instead of firing a separate /save-answers request per category.
     */
    getChangedQuestionary(): Questionary | null {
        if (!this.isDirty()) return null;
        // this.questions are clones produced by orderedQuestionsAsc and bound to the inputs; assigning
        // through the answers setter copies their edited values back onto the questionary's own questions
        // so the serialized payload reflects what the operator typed. Not a redundant self-assignment —
        // removing it would drop the edits from the request.
        this.questionary.answers = this.questions.map(q => q.answer);
        // Freeze exactly what we're submitting so markSaved() re-baselines to this, not to live state.
        this.pendingSnapshot = this.answersSnapshot();
        return this.questionary;
    }

    /** Re-baseline the snapshot after a successful save so unchanged answers aren't re-submitted. */
    markSaved(): void {
        if (!this.answersLoaded) return;
        // Re-baseline to the values that were actually submitted, not to live state — an answer edited
        // while the save was in flight stays dirty and is re-submitted on the next save.
        this.originalAnswersSnapshot = this.pendingSnapshot ?? this.answersSnapshot();
        this.pendingSnapshot = null;
    }
}

</script>
